import React from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import '@/scss/components/_module-skeleton.scss';

interface ModuleSkeletonProps {
  type: string;
}

/**
 * Generic skeleton component for React modules to prevent CLS during lazy loading.
 * Provides different skeleton layouts based on module type.
 */
const ModuleSkeleton = ({ type }: ModuleSkeletonProps) => {
  switch (type) {
    case 'sectionFacet':
    case 'sectionSearchListingFacet':
    case 'productSectionFacet':
      return <SectionFacetSkeleton />;

    case 'listing':
    case 'searchListing':
    case 'productListing':
      return <ListingSkeleton />;

    default:
      return <DefaultSkeleton />;
  }
};

/**
 * Skeleton for section facet / filter components
 */
const SectionFacetSkeleton = () => (
  <div className="module-skeleton module-skeleton--facet" aria-hidden="true">
    {/* Search input skeleton */}
    <div className="module-skeleton__search">
      <Skeleton height={20} width={100} style={{ marginBlockEnd: '8px' }} />
      <Skeleton height={48} width="100%" />
    </div>

    {/* Filter title skeleton */}
    <Skeleton height={24} width={120} style={{ marginBlock: '16px' }} />

    {/* Facet sections skeleton */}
    <div className="module-skeleton__section">
      <Skeleton height={48} width="100%" />
      <div className="module-skeleton__facets">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} height={32} width="100%" style={{ marginBlockEnd: '12px' }} />
        ))}
      </div>
    </div>
  </div>
);

/**
 * Skeleton for listing components
 */
const ListingSkeleton = () => (
  <div className="module-skeleton module-skeleton--listing" aria-hidden="true">
    {/* Results count skeleton */}
    <Skeleton height={20} width={200} style={{ marginBlockEnd: '16px' }} />

    {/* Listing items skeleton */}
    <div className="module-skeleton__items">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="module-skeleton__item">
          <div className="module-skeleton__item-content">
            <Skeleton height={24} width="60%" />
            <Skeleton count={2} height={16} />
            <div className="module-skeleton__item-tags">
              <Skeleton width={60} height={20} />
              <Skeleton width={80} height={20} />
            </div>
          </div>
          <div className="module-skeleton__item-image">
            <Skeleton height="100%" width="100%" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/**
 * Default skeleton for unknown module types
 */
const DefaultSkeleton = () => (
  <div className="module-skeleton module-skeleton--default" aria-hidden="true">
    <Skeleton height={200} width="100%" />
  </div>
);

export default ModuleSkeleton;
